import { DAEMON_URL } from "../config";
import { parseSseStream } from "./sse";
import { HttpError } from "./errors";
import { readError } from "./http";
import type { StreamEvent, ChatRequest } from "./types";

/** Re-exported so existing importers of `client.ts` keep working; the reader lives in `http.ts`. */
export { readError } from "./http";

export async function* sendChat(
  req: ChatRequest,
  signal?: AbortSignal,
): AsyncGenerator<StreamEvent, void, void> {
  const url = `${DAEMON_URL}/chat/${encodeURIComponent(req.project)}`;
  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "text/event-stream",
      },
      credentials: "include",
      // `images` is undefined on a text-only turn, so JSON.stringify drops the
      // key and the body stays byte-identical to what it sent before images.
      // `resumeQueue` is sent only when set, for the same reason.
      body: JSON.stringify({
        message: req.message,
        conversationId: req.conversationId,
        images: req.images,
        ...(req.resumeQueue ? { resumeQueue: true } : {}),
      }),
      signal,
    });
  } catch (e) {
    // An intentional abort (Stop / superseded turn / unmount) must propagate as
    // an AbortError so the caller can treat it as a supersede, NOT emit a scary
    // "connection lost" error frame.
    if ((e as Error)?.name === "AbortError" || signal?.aborted) throw e;
    yield { type: "error", error: `network error: ${(e as Error).message}` };
    return;
  }

  if (res.status === 401) {
    throw new HttpError(401, "unauthorized");
  }
  if (res.status === 403) {
    throw new HttpError(403, "forbidden");
  }
  if (res.status !== 200) {
    const { message, code } = await readError(res);
    throw new HttpError(res.status, message, code);
  }
  if (!res.body) {
    yield { type: "error", error: "empty response body" };
    return;
  }

  yield* parseSseStream(res.body);
}

export async function stopChat(
  project: string,
  conversationId?: number,
  turnId?: number,
): Promise<{ interrupted: boolean; cancelled?: boolean }> {
  const url = `${DAEMON_URL}/chat/${encodeURIComponent(project)}/stop`;
  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers: { "Accept": "application/json", "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(
        conversationId !== undefined
          ? { conversationId, ...(turnId !== undefined ? { turnId } : {}) }
          : {},
      ),
    });
  } catch (e) {
    throw new Error(`network error: ${(e as Error).message}`);
  }
  if (res.status === 401) throw new HttpError(401, "unauthorized");
  if (res.status === 403) throw new HttpError(403, "forbidden");
  if (res.status !== 200) {
    const { message, code } = await readError(res);
    throw new HttpError(res.status, message, code);
  }
  return (await res.json()) as { interrupted: boolean; cancelled?: boolean };
}
